import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { CreditCard, ShieldCheck, Loader2, ExternalLink, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  not_started: { label: 'Not set up', tone: 'bg-white/10 text-gray-300' },
  incomplete: { label: 'Onboarding incomplete', tone: 'bg-amber-500/15 text-amber-400' },
  pending: { label: 'Stripe is verifying your info', tone: 'bg-sky-500/15 text-sky-400' },
  ready: { label: 'Ready for payouts', tone: 'bg-emerald-500/15 text-emerald-400' },
};

// Member-facing Stripe Connect onboarding card. Bank details, identity and
// tax info are all submitted DIRECTLY to Stripe on its hosted flow — the
// platform never sees or stores them.
export default function StripeConnectCard() {
  const { getAuthHeader } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  const refreshStatus = useCallback(async () => {
    setChecking(true);
    try {
      const res = await axios.get(`${API_URL}/api/connect/status`, { headers: getAuthHeader() });
      setStatus(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not check Stripe status');
    } finally {
      setChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshStatus();
    if (searchParams.get('connect') === 'return') {
      toast.success('Welcome back — checking your Stripe onboarding status…');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/connect/onboard`,
        { origin_url: window.location.origin },
        { headers: getAuthHeader() },
      );
      window.location.href = res.data.url; // Stripe-hosted onboarding
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start Stripe onboarding');
      setBusy(false);
    }
  };

  const meta = STATUS_META[status?.status] || STATUS_META.not_started;
  const ready = status?.status === 'ready';

  return (
    <div className="wm-card p-6 rounded-lg" data-testid="stripe-connect-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-gold" /> Payouts via Stripe
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Commission payouts are deposited to your bank through Stripe. Your bank details,
            identity verification and tax information are submitted directly to Stripe — we never
            see or store them.
          </p>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-medium ${meta.tone}`} data-testid="stripe-connect-status">
          {checking ? 'Checking…' : meta.label}
        </span>
      </div>

      {!ready && status?.status === 'pending' && (
        <p className="text-xs text-sky-400 mt-4">
          Your details are submitted — Stripe is verifying them. This usually takes a few minutes to a day.
        </p>
      )}
      {status?.requirements_due > 0 && (
        <p className="text-xs text-amber-400 mt-4">
          Stripe needs {status.requirements_due} more item{status.requirements_due === 1 ? '' : 's'} from you — continue below.
        </p>
      )}

      <div className="flex items-center gap-3 mt-5 flex-wrap">
        {!ready && (
          <Button onClick={startOnboarding} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="stripe-onboard-btn">
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
            ) : (
              <><ExternalLink className="w-4 h-4 mr-2" /> {status?.status === 'not_started' ? 'Set up payouts with Stripe' : 'Continue Stripe onboarding'}</>
            )}
          </Button>
        )}
        <Button variant="outline" onClick={refreshStatus} disabled={checking} className="border-border" data-testid="stripe-status-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} /> Refresh status
        </Button>
      </div>

      {ready && (
        <p className="text-xs text-emerald-400 mt-4 flex items-center gap-1.5" data-testid="stripe-ready-note">
          <ShieldCheck className="w-3.5 h-3.5" /> You're all set — payouts of $25 or more are sent to your Stripe account and deposit to your bank after a short security hold.
        </p>
      )}
    </div>
  );
}
